"use client";

import React, { useState, useEffect } from "react";
import { ArrowRight, Star, Zap } from "lucide-react";
import Link from "next/link";
import { DynamicIcon } from "@/components/ui/DynamicIcon";

// Opaklık gerektiren özel alanlar için hex çevirici (Arkaplan blur'ları vb. için)
const hexToRgba = (hex: string, alpha = 1) => {
    let r = 0, g = 0, b = 0;
    if (hex && (hex.length === 4 || hex.length === 7)) {
        if (hex.length === 4) {
            r = parseInt("0x" + hex[1] + hex[1]); g = parseInt("0x" + hex[2] + hex[2]); b = parseInt("0x" + hex[3] + hex[3]);
        } else if (hex.length === 7) {
            r = parseInt("0x" + hex[1] + hex[2]); g = parseInt("0x" + hex[3] + hex[4]); b = parseInt("0x" + hex[5] + hex[6]);
        }
    }
    return `rgba(${r},${g},${b},${alpha})`;
};

// Dışarıdan Beklediğimiz Verilerin Tipleri (Artık products prop'u almıyor)
interface HeroModernProps {
    title: string;
    subtitle: string;
    primaryColor: string;
    yearsOfExperience: number;
}

export default function HeroModern({
    title,
    subtitle,
    primaryColor,
    yearsOfExperience
}: HeroModernProps) {
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
    const [windowSize, setWindowSize] = useState({ width: 0, height: 0 });

    // 📌 SENİN İSTEDİĞİN SABİT ÜRÜN LİSTESİ
    const fixedProducts = [
        { title: "Trafik Sigortası", icon: "Car", link: "/teklif-al/trafik-sigortasi", badge: "Hızlı" },
        { title: "Kasko", icon: "Shield", link: "/teklif-al/kasko", badge: null },
        { title: "Sağlık Sigortası", icon: "Stethoscope", link: "/teklif-al/tamamlayici-saglik-sigortasi", badge: "Popüler" },
        { title: "DASK Sigortası", icon: "Building2", link: "/iletisim", badge: null },
        { title: "Konut Sigortası", icon: "Home", link: "/iletisim", badge: null },
        { title: "Seyahat Sigortası", icon: "Plane", link: "/iletisim", badge: "Yeni" },
    ];

    // Başlığı parçalama işlemi (Son kelimeyi renklendirmek için)
    const words = title.trim().split(/\s+/);
    const heroTitleLast = words.length > 1 ? words.pop() : "";
    const heroTitleFirst = words.join(" ");

    // Animasyon (Parallax) Efektleri İçin Fare Takibi
    useEffect(() => {
        setWindowSize({ width: window.innerWidth, height: window.innerHeight });

        const handleMouseMove = (e: MouseEvent) => setMousePos({ x: e.clientX, y: e.clientY });
        const handleResize = () => setWindowSize({ width: window.innerWidth, height: window.innerHeight });

        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const calcTranslate = (ratio: number) => {
        if (windowSize.width === 0) return "translate(0, 0)";
        const x = (mousePos.x - windowSize.width / 2) * ratio;
        const y = (mousePos.y - windowSize.height / 2) * ratio;
        return `translate(${x}px, ${y}px)`;
    };

    return (
        <section className="relative min-h-screen w-full flex items-center justify-center pt-24 pb-12 overflow-hidden bg-[#fafafa]">

            {/* HARMANLANMIŞ ARKA PLAN: Yaşayan Sahne Silüeti */}
            <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
                <div className="absolute inset-0 bg-linear-to-b from-white via-white/80 to-[#fdf0e9]/20" style={{ transform: calcTranslate(0.01) }}></div>

                {/* Orta Plan İllüstrasyonu */}
                <div className="absolute inset-0 flex items-end justify-center pb-32 opacity-[0.15] md:opacity-[0.25]" style={{ transform: calcTranslate(0.02) }}>
                    <svg viewBox="0 0 1440 600" className="w-[115%] h-auto" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M720 150L600 250V550H840V250L720 150Z" fill="white" stroke="#ccc" strokeWidth="1" />
                        <rect x="780" y="180" width="30" height="60" fill="white" stroke="#ccc" strokeWidth="1" />
                        <circle cx="795" cy="160" r="10" fill="#ddd" className="animate-pulse" />
                        <rect x="650" y="300" width="140" height="100" fill="#fff" stroke="#ccc" strokeWidth="1" />
                        <path d="M400 450H550L580 490H370L400 450Z" fill="white" stroke="#ccc" strokeWidth="1" />
                        <circle cx="410" cy="550" r="15" fill="white" stroke="#ccc" strokeWidth="1" />
                        <circle cx="540" cy="550" r="15" fill="white" stroke="#ccc" strokeWidth="1" />
                    </svg>
                </div>

                {/* Yumuşak Işık Katmanı */}
                <div className="absolute inset-0" style={{ transform: calcTranslate(0.04) }}>
                    <div className="absolute top-[10%] left-[5%] w-72 h-72 rounded-full blur-[100px]" style={{ backgroundColor: hexToRgba(primaryColor, 0.15) }}></div>
                    <div className="absolute bottom-[5%] right-[5%] w-96 h-96 rounded-full blur-[120px]" style={{ backgroundColor: hexToRgba(primaryColor, 0.1) }}></div>
                </div>
            </div>

            <div className="container relative z-10 mx-auto px-4">
                <div className="flex flex-col items-center max-w-5xl mx-auto">

                    {/* DİNAMİK BAŞLIK */}
                    <div className="text-center mb-12">
                        <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-slate-800 leading-none tracking-tighter mb-6">
                            {heroTitleFirst} <span style={{ color: primaryColor }}>{heroTitleLast}</span>
                        </h1>

                        <p className="text-lg md:text-xl text-slate-500 max-w-2xl mx-auto font-medium leading-relaxed">
                            {subtitle}
                        </p>
                    </div>

                    {/* SABİT ÜRÜN GRID (fixedProducts dizisinden geliyor) */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 w-full mb-12">
                        {fixedProducts.map((p, index) => (
                            <Link
                                href={p.link}
                                key={index}
                                className="group relative bg-white/70 backdrop-blur-md border border-white hover:border-slate-200 rounded-3xl p-6 flex flex-col items-center justify-center transition-all duration-500 hover:shadow-2xl hover:bg-white hover:-translate-y-2 shadow-[0_10px_30px_rgba(0,0,0,0.03)]"
                            >
                                {p.badge && (
                                    <span
                                        className="absolute top-4 right-4 text-[9px] font-black px-2.5 py-1 rounded-lg text-white shadow-sm"
                                        style={{ backgroundColor: primaryColor }}
                                    >
                                        {p.badge}
                                    </span>
                                )}

                                <div
                                    className="w-14 h-14 md:w-16 md:h-16 rounded-2xl flex items-center justify-center mb-5 transition-all duration-500 group-hover:scale-110 group-hover:rotate-3 shadow-inner"
                                    style={{ backgroundColor: hexToRgba(primaryColor, 0.08) }}
                                >
                                    {/* DynamicIcon string değerleri destekler */}
                                    <DynamicIcon name={p.icon} size={28} className="md:w-8 md:h-8" style={{ color: primaryColor }} />
                                </div>

                                <h3 className="font-bold text-slate-800 text-center text-lg md:text-xl mb-2 group-hover:text-black">
                                    {p.title}
                                </h3>

                                <div
                                    className="text-sm font-bold flex items-center group-hover:translate-y-0 transition-all duration-300"
                                    style={{ color: primaryColor }}
                                >
                                    Teklif Al <ArrowRight size={12} className="ml-1.5" />
                                </div>
                            </Link>
                        ))}
                    </div>

                    {/* ALT GÜVEN BARI (Dinamik Deneyim Yılı) */}
                    <div className="w-full flex flex-col md:flex-row items-center justify-center gap-6 py-8 border-t border-slate-100">
                        <div className="flex items-center gap-3">
                            <Star size={16} style={{ color: primaryColor }} />
                            <span className="text-sm font-bold text-slate-600">{yearsOfExperience} Yıllık Deneyim</span>
                        </div>
                        <div className="hidden md:block w-px h-6 bg-slate-200"></div>
                        <div className="flex items-center gap-2 text-sm font-bold text-slate-600">
                            <Zap size={16} style={{ color: primaryColor }} />
                            <span>Kesintisiz 7/24 Destek</span>
                        </div>
                        <div className="hidden md:block w-px h-6 bg-slate-200"></div>
                        <Link
                            href="/iletisim"
                            className="text-sm font-black flex items-center gap-2 transition-colors hover:opacity-70"
                            style={{ color: primaryColor }}
                        >
                            Uzmanımıza Danışın <ArrowRight size={16} />
                        </Link>
                    </div>

                </div>
            </div>
        </section>
    );
}